import { useId, useState } from 'react'
import { AlertTriangle, Archive } from 'lucide-react'
import { Button, TreeNav } from '@krp/ui-kit'
import { formatInstant, formatKopecks } from '../../../shared/uc1b/format'
import { QueryState } from '../../../shared/uc1b/QueryState'
import { isFolderNode, shiftNode } from '../model/tree'
import type { ReceiptsNavVm } from '../model/types'

/** Короткий выбор магазина; кассы и информативные смены — ниже, в подробном выборе. */
export function ReceiptsPlacePicker({ nav, onSelect, onToggle, onRetry }: {
  nav: ReceiptsNavVm; onSelect: (id: string) => void; onToggle: (id: string) => void; onRetry: () => void
}) {
  const name = useId()
  const [limit, setLimit] = useState(20)
  const shifts = nav.shifts?.shifts ?? []
  return <div className="aqsi-receipts-place">
    <div role="radiogroup" aria-label="Магазин" className="aqsi-receipts-place__stores">
      {(nav.stores ?? []).map((store) => <label key={store.id} data-selected={store.id === nav.selectedId || undefined}>
        <input type="radio" name={name} checked={store.id === nav.selectedId} onChange={() => onSelect(store.id)} /><span>{store.label}</span>
      </label>)}
    </div>
    <p className="aqsi-receipts-place__selection">Выбрано: {nav.summaryText}</p>
    <details>
      <summary>Подробный выбор кассы и смены</summary>
      <TreeNav label="Где смотрим: магазины и кассы" nodes={nav.nodes.filter((node) => !node.id.startsWith('shift:'))}
        onSelect={(id) => isFolderNode(id) ? onToggle(id) : onSelect(id)} onToggle={onToggle} />
      <section aria-label="Смены выбранной кассы">
        <h3>Смены кассы</h3>
        {nav.shiftsQuery !== undefined && <QueryState query={nav.shiftsQuery} emptyText="У кассы пока нет смен" onRetry={onRetry} />}
        {nav.shifts === undefined ? <p>Выберите кассу выше, чтобы увидеть её смены.</p> : <>
          <p>Кассиры, суммы и период чеков — по сохранённому журналу. Это не время открытия и закрытия смены.</p>
          {nav.shifts.state.warning !== null && <p role="status">{nav.shifts.state.warning}</p>}
          <ul className="aqsi-receipts-place__shifts">{shifts.slice(0, limit).map((shift) => {
            const id = shiftNode(nav.shifts!.registerId, shift.number)
            const journal = shift.journal
            return <li key={id} data-selected={id === nav.selectedId || undefined}>
              <Button variant="ghost" aria-pressed={id === nav.selectedId} onClick={() => onSelect(id)}>Смена № {shift.number}</Button>
              <span>{shift.isOpen === true ? 'Открыта' : shift.isOpen === false ? 'Закрыта' : 'Состояние открытия не получено'}</span>
              {id !== nav.selectedId && <small>Выберите смену, чтобы прочитать её открытие и закрытие из кабинета.</small>}
              {shift.openedAt !== null && <p>Открытие: {formatInstant(shift.openedAt)} · {shift.openedByCashier ?? 'кассир не указан'}</p>}
              {shift.closedAt !== null && <p>Закрытие: {formatInstant(shift.closedAt)} · {shift.closedByCashier ?? 'кассир не указан'}</p>}
              {shift.cleanup?.state === 'cleared' && <p className="aqsi-receipts-place__cleanup" data-state="cleared" role="status">
                <Archive size={14} aria-hidden="true" /> Смена очищена из журнала программы {formatInstant(shift.cleanup.at)}: {shift.cleanup.count} чеков · Приход{' '}
                {formatKopecks(shift.cleanup.saleKopecks)} · Возврат {formatKopecks(shift.cleanup.refundKopecks)}. Чеки смены показываются из кабинета AQSI.
              </p>}
              {shift.cleanup?.state === 'problem' && <p className="aqsi-receipts-place__cleanup" data-state="problem" role="status">
                <AlertTriangle size={14} aria-hidden="true" /> Чеки смены не очищаются из журнала: {shift.cleanup.reason ?? 'причина не указана'}
              </p>}
              {journal == null ? shift.cleanup?.state === 'cleared' ? null : <p>Чеков этой смены в журнале нет.</p> : <>
                <p>{journal.cashiers.length === 0 ? 'Кассир не указан' : journal.cashiers.join(', ')}</p>
                <p>Первый чек: {formatInstant(journal.firstReceiptAt)}<br />Последний чек: {formatInstant(journal.lastReceiptAt)}</p>
                <p>{journal.count} чеков · Приход {formatKopecks(journal.saleKopecks)} · Возврат {formatKopecks(journal.refundKopecks)}</p>
              </>}
              {shift.updatedAt !== null && <small>Обновлено в кабинете: {formatInstant(shift.updatedAt)}</small>}
            </li>
          })}</ul>
          {shifts.length > limit && <Button onClick={() => setLimit((current) => current + 20)}>Ещё смены ({shifts.length - limit})</Button>}
        </>}
      </section>
    </details>
  </div>
}
